import { useState, type ChangeEvent } from "react";
import { IconLock } from "@tabler/icons-react";
import Card from "../UI/Card";
import Title from "../UI/Title";
import Text from "../UI/Text";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
// @ts-expect-error pure js
import TextInputStandalone from "../UI/TextInputStandalone";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import { useToast } from "../UI/Toast/useToast";
import useCanWrite from "../../hooks/use-can-write";
import {
  getLandlordInterestOption,
  getVisitStatusOption,
  LANDLORD_INTEREST_OPTIONS,
  toDateTimeLocal,
  VISIT_STATUS_OPTIONS,
  type FollowUp,
} from "../../utils/candidature";
import type {
  LandlordInterest,
  Tenant,
  VisitStatus,
} from "../../models/tenant";
import styles from "./CandidatSuiviCard.module.css";

interface CandidatSuiviCardProps {
  tenant: Tenant;
}

export default function CandidatSuiviCard({ tenant }: CandidatSuiviCardProps) {
  const toast = useToast();
  const canWrite = useCanWrite();
  const { updateTenant } = useTenants() as {
    updateTenant: (id: number, data: Partial<Tenant>) => Promise<unknown>;
  };
  const [visitDate, setVisitDate] = useState(() =>
    toDateTimeLocal(tenant.visit_date),
  );

  async function save(data: FollowUp) {
    try {
      await updateTenant(tenant.id, data);
    } catch (error) {
      toast.error({
        title: "Impossible d'enregistrer le suivi de la candidature",
        description: handleAPIError(error),
      });
    }
  }

  function saveVisitDate(value: string) {
    setVisitDate(value);
    const stored = toDateTimeLocal(tenant.visit_date);
    if (value === stored || (value !== "" && value.length < 16)) return;
    void save({ visit_date: value === "" ? null : value });
  }

  return (
    <Card padding="md" className={styles.card}>
      <div className={styles.header}>
        <Title variant="deca" weight="bold" as="h2">
          Suivi de la candidature
        </Title>
        <Text as="div" variant="deci" className={styles.privateHint}>
          <IconLock size={14} />
          Visible uniquement par vous, comme vos notes
        </Text>
      </div>
      <div className={styles.fields}>
        <ReactSelectStandalone
          label="Votre avis sur ce dossier"
          name="landlord_interest"
          options={LANDLORD_INTEREST_OPTIONS}
          value={getLandlordInterestOption(tenant.landlord_interest)}
          isDisabled={!canWrite}
          onChange={(option: { value: LandlordInterest | "" } | null) =>
            void save({ landlord_interest: option?.value || null })
          }
        />
        <ReactSelectStandalone
          label="Visite"
          name="visit_status"
          options={VISIT_STATUS_OPTIONS}
          value={getVisitStatusOption(tenant.visit_status)}
          isDisabled={!canWrite}
          onChange={(option: { value: VisitStatus | "" } | null) =>
            void save({ visit_status: option?.value || null })
          }
        />
        <TextInputStandalone
          label="Date de visite"
          name="visit_date"
          type="datetime-local"
          value={visitDate}
          disabled={!canWrite}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            saveVisitDate(event.target.value)
          }
        />
      </div>
    </Card>
  );
}
